import { PencilIcon, XIcon } from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'

import { Button, DateInput, IconButton, Popover, Tooltip } from 'slab'
import { parseDateOnly } from 'utils'

import { type EmployeeInfo } from './BulkOffboardingContext'

export function MuteAction ({
  employee,
  onMute,
  onUnmute,
  disabled,
  isPayrollDeactivated,
}: {
  employee: EmployeeInfo
  onMute: (employeeId: string, date: string) => void | Promise<void>
  onUnmute: (employeeId: string) => void
  disabled: boolean
  isPayrollDeactivated: boolean
}) {
  const [open, setOpen] = useState(false)
  const [muteDate, setMuteDate] = useState('')
  const [calendarPortalContainer, setCalendarPortalContainer] = useState<HTMLElement | null>(null)
  const dateInputRef = useRef<HTMLInputElement>(null)

  const minDate = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    return d.toISOString().split('T')[0]
  }, [])

  const isDateValid = muteDate >= minDate
  const hasLeaveDate = !!employee.silence_pending_termination_until

  const handleMuteSubmit = useCallback(async () => {
    if (!muteDate || !isDateValid) return
    await onMute(employee.id, muteDate)
    setMuteDate('')
    setOpen(false)
  }, [muteDate, isDateValid, onMute, employee.id])

  const handleRemoveLeave = useCallback(() => {
    if (disabled) return
    onUnmute(employee.id)
  }, [disabled, onUnmute, employee.id])

  // Not payroll deactivated: show dash
  if (!isPayrollDeactivated) {
    return <span className='text-gray-500'>—</span>
  }

  // Payroll deactivated, no leave date: show "Not on leave" + pencil icon
  if (!hasLeaveDate) {
    const popoverContent = (
      <div
        ref={(el) => {
          if (el) setCalendarPortalContainer(el)
        }}
        className='p-4 flex flex-col gap-3 w-64'
      >
        <p className='body-sm-bold'>On leave until</p>
        <DateInput
          ref={dateInputRef}
          portalContainer={calendarPortalContainer ?? undefined}
          value={muteDate}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMuteDate(e.target.value)}
          min={minDate}
        />
        {muteDate && !isDateValid && (
          <p className='text-red-500 text-xs'>Date must be in the future</p>
        )}
        <Button
          variant='secondary'
          size='small'
          disabled={!muteDate || !isDateValid}
          onClick={handleMuteSubmit}
        >
          Save
        </Button>
      </div>
    )

    return (
      <div className='flex gap-2'>
        <span>Not on leave</span>
        <Popover
          align='end'
          open={open}
          onOpenChange={setOpen}
          side='bottom'
          trigger={
            <IconButton
              icon={PencilIcon}
              label='Set leave date'
              size='small'
              disabled={disabled}
              onClick={() => setOpen(true)}
            />
          }
        >
          {popoverContent}
        </Popover>
      </div>
    )
  }

  // Payroll deactivated, has leave date: show formatted date + X icon
  const formattedDate = parseDateOnly(employee.silence_pending_termination_until!).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })

  return (
    <div className='flex gap-2'>
      <span>{formattedDate}</span>
      <Tooltip
        content="Remove this employee's leave"
        side='top'
        align='center'
      >
        <IconButton
          icon={XIcon}
          label='Remove leave date'
          size='small'
          disabled={disabled}
          onClick={handleRemoveLeave}
        />
      </Tooltip>
    </div>
  )
}
